import React, { useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import HistoricalImportPanel from "./HistoricalImport";

function ImportBlock({ kind, label, endpoint, description }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!file) return toast.error("Choose a file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.inserted}, skipped ${data.skipped}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Backend endpoint requires auth; use fetch with cookies
    const token = localStorage.getItem("access_token");
    fetch(`${API_BASE}/imports/templates/${kind}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${kind}_template.csv`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Failed to download template"));
  };

  return (
    <Card className="border-slate-200 shadow-none p-6 space-y-3">
      <div>
        <div className="font-heading font-medium">{label}</div>
        <div className="text-sm text-slate-500 mt-1">{description}</div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid={`download-${kind}-template`}>
          <Download className="w-4 h-4 mr-2" />Download CSV template
        </Button>
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid={`file-${kind}`}
          className="text-sm"
        />
        <Button
          className="bg-brand hover:bg-brand-hover"
          onClick={upload}
          disabled={uploading || !file}
          data-testid={`upload-${kind}-btn`}
        >
          <Upload className="w-4 h-4 mr-2" />{uploading ? "Uploading…" : "Upload"}
        </Button>
      </div>
      {result && (
        <div className="text-sm border-t border-slate-100 pt-3">
          <div>
            <span className="text-emerald-700 font-medium">Inserted: {result.inserted}</span>
            {" · "}
            <span className="text-amber-700 font-medium">Skipped: {result.skipped}</span>
          </div>
          {result.errors?.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-slate-500">{result.errors.length} skip reasons</summary>
              <ul className="mt-2 text-xs text-slate-500 space-y-1 max-h-40 overflow-auto">
                {result.errors.map((e, i) => <li key={`row${e.row}-${i}`}>Row {e.row}: {e.reason}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}

export default function DataImport() {
  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight">Data Import</h1>
        <p className="text-sm text-slate-500 mt-1">Bulk-load historical clients, projects, and payments from CSV or Excel.</p>
      </div>

      <Tabs defaultValue="historical">
        <TabsList>
          <TabsTrigger value="historical" data-testid="tab-historical">Historical (2-step)</TabsTrigger>
          <TabsTrigger value="clients">Clients</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="historical" className="mt-4">
          <HistoricalImportPanel />
        </TabsContent>
        <TabsContent value="clients" className="mt-4">
          <ImportBlock
            kind="clients" label="Clients"
            endpoint="/api/imports/clients"
            description="Columns (case-insensitive): firm_name (required), contact_person_name, email, phone, country, address, default_currency, tax_number, notes. Duplicate firm names are skipped."
          />
        </TabsContent>
        <TabsContent value="projects" className="mt-4">
          <ImportBlock
            kind="projects" label="Projects"
            endpoint="/api/imports/projects"
            description="Columns: firm_name (required — must already exist), project_name (required), project_amount (required, > 0), currency, status, project_start_date, expected_submission_date, bd_email, project_code (optional; leave blank to auto-generate in new TS-XX/n/YY-YY/MMnnn format), stage1_name/stage1_pct … stage4_name/stage4_pct."
          />
        </TabsContent>
        <TabsContent value="payments" className="mt-4">
          <ImportBlock
            kind="payments" label="Payments"
            endpoint="/api/imports/payments"
            description="Columns: invoice_number (required — must already exist), payment_date, amount_received (required, > 0), currency, amount_received_inr (actual INR credited to bank), payment_method, reference, notes."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
